import { AppData, Lightning, Router } from '@lightningjs/sdk';
import { Episode, Video, Show } from 'types/api/media';
import { ViewContext } from 'types/analytics';
import {
  getCreditStartTime,
  isEpisode,
  isMovie,
  isSeries,
  isShow,
  isVideo,
} from 'support/contentUtils';
import { RoutePath } from 'config/routes';
import { debounceAnnounce } from 'support/generalUtils';
import { STANDARD_FADE } from 'support/animations';
import { constants } from 'aliases';
import { translate } from 'support/translate';
import { formatVideoTime } from 'support/contentUtils';

import EndCard from 'components/pages/playback/EndCard/EndCard';
import { minutesToMilliseconds } from 'support/timeUtils';
import { navigateDetailsFromVideo, navigateToPlay } from 'support/routerUtils';
import { ListItemContext } from 'types/events';
import { AdCuePoint } from 'components/pages/playback/ad-managers/AbstractAdManager';
import BrightcovePlayer from 'components/pages/playback/player-components/BrightcovePlayer';
import AdCountdown from 'components/pages/playback/overlay/AdCountdown';
import InteractiveAdManager from '../interactive-ads/InteractiveAdManager';
import {
  reportInteractiveAdExit,
  reportInteractiveAdSlotComplete,
  reportInteractiveAdSlotStart,
  reportPlayRecommendation,
} from 'services/analytics/reportingServiceVideo';
import {
  InteractiveAdCompleteType,
  InteractiveAdData,
} from '../interactive-ads/InteractiveAdUtils';
import AbstractPlaybackPage, {
  AbstractPlaybackPageTemplateSpec,
} from './AbstractPlaybackPage';
import PauseAd from '../pauseAd/PauseAd';
import VodPlayerEventHandler from 'components/pages/playback/PlayerEventHandler/VodPlayerEventHandler';

const AD_COUNTDOWN_X = (1920 - constants.ui.progressBarWidth) / 2;

export interface VodPlaybackPageTemplateSpec
  extends AbstractPlaybackPageTemplateSpec {
  Content: {
    AdCountdown: typeof AdCountdown;
    EndCard: typeof EndCard;
    PauseAd: typeof PauseAd;
    InteractiveAdManager: typeof InteractiveAdManager;
  } & AbstractPlaybackPageTemplateSpec['Content'];
}

export default class VodPlaybackPage
  extends AbstractPlaybackPage
  implements
    Lightning.Component.ImplementTemplateSpec<VodPlaybackPageTemplateSpec>
{
  private _VodContent = (this as any).getByRef('Content')!;
  private _AdCountdown = this._VodContent.getByRef('AdCountdown')!;
  private _EndCard = this._VodContent.getByRef('EndCard')!;
  private _PauseAd = this._VodContent.getByRef('PauseAd')!;
  private _InteractiveAdManager = this._VodContent.getByRef(
    'InteractiveAdManager',
  )!;

  private _validEndCard = false;
  private _endCardShown = false;

  protected static override PlayerEventHandlerType = VodPlayerEventHandler;

  static override _template(): Lightning.Component.Template<VodPlaybackPageTemplateSpec> {
    const parentTemplate = super._template();
    return {
      ...parentTemplate,
      Content: {
        ...parentTemplate.Content,
        AdCountdown: {
          type: AdCountdown,
          x: 1920 - AD_COUNTDOWN_X,
          y: 90,
          mountX: 1,
          visible: false,
        },
        EndCard: {
          type: EndCard,
          alpha: 0,
          transitions: STANDARD_FADE,
          signals: {
            backToSeries: '_backToSeries',
            watchCredits: '_closeEndCard',
            play: '_playAnotherContent',
          },
        },
        PauseAd: {
          type: PauseAd,
        },
        InteractiveAdManager: {
          type: InteractiveAdManager,
          signals: {
            interactiveAdEnd: '$interactiveAdEnd',
            interactiveAdExit: '$interactiveAdExit',
            interactiveAdStarted: '$interactiveAdStarted',
          },
        },
      },
    };
  }

  get announce() {
    // Announce ad countdown timer if available
    if (this._AdCountdown.visible) {
      return this._AdCountdown.announce;
    }
  }

  override _setup() {
    super._setup();
    this._PauseAd.onLoad = () => this._setState('PauseAd');
  }

  override _captureKey(event: KeyboardEvent) {
    super._captureKey(event);

    if (!this._Player.isPlaying() && this._getState() !== 'PauseAd') {
      this.startPauseAd();
    }

    // Ad skip key for dev only
    if (event.key === '0' && !!AppData?.adSkipEnabled()) {
      this._Player.skipCurrentAd();
    }

    return false;
  }

  override _handleMediaFastForward() {
    if (this._isAdPlaying || this._getState() === 'PauseAd') return;
    this._setState('Interactive');
    this._PauseAd.stop();
    this._VideoOverlay.handleMediaFFRW('FF');
  }

  override _handleMediaRewind() {
    if (this._isAdPlaying || this._getState() === 'PauseAd') return;
    this._setState('Interactive');
    this._PauseAd.stop();
    this._VideoOverlay.handleMediaFFRW('RW');
  }

  override _handleClick() {
    if (this._getState() === 'PauseAd') return;
    this._setState('Interactive');
  }

  override $handleAdStateChange(isAdPlaying: boolean) {
    this._isAdPlaying = isAdPlaying;
    this._VideoOverlay.setIsAdPlaying(isAdPlaying);

    if (isAdPlaying) {
      this._handleAdPodStart();
    } else {
      this._handleAdPodEnd();
    }
  }

  override $videoPlayerEnded() {
    super.$videoPlayerEnded();
    this._showEndCardOrAysw();
  }

  override $videoPlayerPlay() {
    super.$videoPlayerPlay();
    this._PauseAd.stop();

    // Edge case where the pause ad might be open but playback starts
    // Puts user control back into a predictable state
    if (this._getState() === 'PauseAd') {
      this._setState('Idle');
    }
  }

  override $videoPlayerPause() {
    super.$videoPlayerPause();
    this.startPauseAd();
  }

  override $videoPlayerSeeking(rawTime: number, relativeTime: number) {
    // Interactive ads introduce seeking within ads for reloading purposes
    if (!this._isAdPlaying) this._VideoOverlay.updateProgress(relativeTime);

    this._PlayerEventHandler.patch({ seekType: null });

    super.$videoPlayerSeeking(rawTime, relativeTime);
  }

  override $videoPlayerTimeUpdate(
    relativeTime: number,
    hasReachedCredits: boolean,
  ) {
    if (!this._Player) return;

    let finalTime = 0;

    if (this._video) {
      if (this._InteractiveAdManager?.isInteractiveAdPlaying) {
        // Ignore time handling during interactive ads because player is unloaded
        return;
      }

      if (hasReachedCredits) {
        this._showEndCardOrAysw();
      }

      finalTime = Math.round(relativeTime);
      this._VideoOverlay.updateProgress(finalTime);

      const player = this._Player;
      const isAdPlaying = this._isAdPlaying;
      const adRemainingTime = player.getRemainingAdTime();

      if (player.shouldSkipAdPod(isAdPlaying)) {
        // Skip ad when it has already been watched and it's just starting
        player.skipAdPod();
      } else if (player.shouldEarlySkipAdPod()) {
        // For Brightcove only - early skip next ad to avoid ad flashing.
        player.earlySkipAdPod();
      } else if (player.shouldCleanupAdPod(isAdPlaying)) {
        // Cleanup ad playing state if ad break ended event is never received
        player.cleanupAdPod();
        this._AdCountdown.visible = false;
      } else if (isAdPlaying && adRemainingTime) {
        // If adRemainingTime is small enough to be considered completed,
        // hide _AdCountdown to avoid stalling at 0 until adBreakEnded event is fired.
        if (adRemainingTime <= 0.1) {
          this._AdCountdown.visible = false;
        } else {
          // Update ad countdown if ad is still playing
          this._AdCountdown.countdown = adRemainingTime;
        }
      } else {
        // Intermittently, time update events can skip IMA's ad detection
        // Force it to verify and retry the ad start timing
        player.verifyCuepointPlaying(player.currentRawTime());
      }
    }

    if (this._shouldAnnounceCurrentTime) {
      this._shouldAnnounceCurrentTime = false;
      this._announceCurrentTime(finalTime);
    }
  }

  override $interactiveAdInit(data: InteractiveAdData) {
    // We must calculate the timing positions before unloading the player
    const adDuration = this._Player.adDuration();
    if (adDuration) {
      const adRemainingTime = this._Player.getRemainingAdTime()!;
      const slotDuration = data.duration;
      const relativeTime =
        this._Player.getRelativeTime() +
        (data.ignoreAdDuration ? slotDuration : 0);

      // Update overlay with values as if the ad completes
      this._VideoOverlay.duration = adDuration;
      this._VideoOverlay.updateProgress(relativeTime);
      this._AdCountdown.countdown = adRemainingTime - slotDuration;
    }

    this._Player.unload();

    this._PauseAd.stop();
    this.toggleOverlayInfo(false);
    this._setState('Idle');

    this.$bufferStart();
    this._InteractiveAdManager?.start(data);
  }

  override $cuePointsChanged(cuePoints: AdCuePoint[] | null) {
    if (!cuePoints) return;

    const parsedCuePoints = cuePoints.map(cuePoint => {
      const player = this._Player;
      const start = player.getContentTimeForStreamTime(cuePoint.start);
      const end = player.getContentTimeForStreamTime(cuePoint.end);
      if (start === null || end === null) return null;

      return {
        start,
        end,
        played: cuePoint.played,
      };
    });

    this._VideoOverlay.updateCuePoints(
      parsedCuePoints.filter(point => point !== null) as AdCuePoint[],
    );
  }

  override _seek(position: number) {
    this._Player.seek(position);
  }

  override _pause() {
    super._pause();
    if (this._AdCountdown.visible) this._AdCountdown.handleAnnounce();
  }

  override _seekEnd() {
    this.startPauseAd();
  }

  override _play() {
    super._play();
    if (this._AdCountdown.visible) this._AdCountdown.handleAnnounce();
  }

  override _openMenu() {
    super._openMenu();
    this._PauseAd.stop();
  }

  override _closeMenu() {
    if (!this._Player.isPlaying()) this.startPauseAd();
  }

  override setupPlayer(useBc: boolean) {
    const { mediaContent, hasDrm, drmLicenseUrl, progress, upNext, token } =
      this.pageData!;

    this._video = mediaContent as Video;
    this._Player.videoId = this._video.guid;
    this._Player.adZone = this._video.adZone;
    this._VideoOverlay.contentType = 'vod';
    this._ayswIdleLimit = minutesToMilliseconds(
      AppData?.ayswIdleTimes?.vod ?? 0,
    );
    this._ayswTimeoutFunction = this.fireVodAyswTimeout.bind(this);

    if (useBc) {
      (this._Player as BrightcovePlayer).token = token;
      (this._Player as BrightcovePlayer).bcVideoId = this._video.bcVideoId;
    }

    if (upNext && upNext.length > 0) {
      this._EndCard.content = { recommended: upNext, current: this._video };
      this._EndCard.patch({ isEnd: false });
      this._validEndCard = true;
      this._endCardShown = false;
    }

    this._hasReachedEnd = false;
    this._isAdPlaying = false;

    this._Player.hasDrm = hasDrm;
    this._Player.drmLicenseUrl = drmLicenseUrl;

    // If the current progress is past the credits, start over
    const hasReachedCredit =
      progress && progress >= getCreditStartTime(this._video as Video);
    this._Player.startTime = hasReachedCredit ? 0 : progress;

    this._AdCountdown.visible = false;

    return true;
  }

  override exitPlayer() {
    this.handlePlayerExit(() => {
      this._AdCountdown.visible = false;
      this.cleanupPlayer();
      this._TextTrackDisplay.reset();

      AppData?.device.onPlayerExit();

      this.navigateAfterExit();
    });
  }

  override getAyswLabel(): { showName: string; cancelLabel: string } {
    const { mediaContent } = this.pageData!;

    let showName = '';
    let cancelLabel = '';

    if (isMovie(mediaContent)) {
      showName = mediaContent.title;
    } else {
      showName = (mediaContent as Episode).seriesName;
    }
    cancelLabel = translate('global.returnToDetails');

    return { showName, cancelLabel };
  }

  override _handleContinueOnAysw() {
    this.resetAyswTimer();
    if (!this._hasReachedEnd) {
      this._Player.resume();
    } else {
      this._showEndCardOrAysw();
    }
  }

  override _handleBackOnAysw() {
    if (!this.pageData) return;
    const { mediaContent } = this.pageData;

    const video = mediaContent as Video;
    const routePath = isSeries(video)
      ? RoutePath.SERIES_DETAIL
      : RoutePath.MOVIE_DETAIL;
    Router.navigate(`${routePath}/${video.showSlug}`);
  }

  override _announceCurrentTime(currentTime: number) {
    this.fireAncestors('$announcerCancel');

    debounceAnnounce(
      this,
      translate('ttsPrompts.playing', formatVideoTime(currentTime)),
    );
  }

  $interactiveAdStarted(adData?: InteractiveAdData) {
    this.$bufferEnd();
    reportInteractiveAdSlotStart(adData?.adType);
  }

  $interactiveAdEnd(event: {
    adCredit: boolean;
    interactiveAdData?: InteractiveAdData;
    adCompleteType: InteractiveAdCompleteType;
  }) {
    const adData = event.interactiveAdData;
    const isCredit = event.adCredit;
    let resumeTime = adData?.cuePoint.end;

    if (isCredit) {
      this._AdCountdown.visible = false;
    } else {
      resumeTime = adData?.adEndTime;
    }

    this._Player.reload(resumeTime);
    this.toggleOverlayInfo(true);
    this._setState('Idle');

    reportInteractiveAdSlotComplete(adData?.adType);
    reportInteractiveAdExit(isCredit, event.adCompleteType, adData?.adType);
  }

  $interactiveAdExit(event: { interactiveAdData?: InteractiveAdData }) {
    reportInteractiveAdExit(
      false,
      InteractiveAdCompleteType.COMPLETE,
      event.interactiveAdData?.adType,
    );

    this.toggleOverlayInfo(true);
    this.exitPlayer();
  }

  private fireVodAyswTimeout() {
    this._shouldPromptAysw = true;
    this._ayswTimer = null;
  }

  private _showEndCard() {
    if (!this._validEndCard) return;
    this._endCardShown = true;
    this._EndCard.patch({ isEnd: this._hasReachedEnd });
    this._setState('EndCard');
  }

  private _closeEndCard() {
    this._setState('Idle');
    this._Player.resume();
  }

  private _showEndCardOrAysw() {
    if (!this._EndCard.hasFocus() && this._shouldPromptAysw) {
      this.showAyswOrEventEnded();
    } else if (
      this._validEndCard &&
      (!this._endCardShown || this._hasReachedEnd)
    ) {
      this._showEndCard();
    }
  }

  private _backToSeries() {
    if (!this._video) return;
    navigateDetailsFromVideo(this._video);
  }

  private _playAnotherContent(
    content: Episode | Show,
    context: ViewContext,
    index: number,
  ) {
    this._hasReachedEnd = true;
    this._closeEndCard();
    this.cleanupPlayer();

    let guid: string | undefined;
    if (isEpisode(content)) {
      guid = (content as Episode).guid;
    } else if (isShow(content)) {
      guid = (content as Show).firstEpisode?.guid;
    }

    if (guid) {
      reportPlayRecommendation('autoPlay', content);
      const images = content.images;
      const listItemContext: ListItemContext = {
        rowIndex: 0,
        itemIndex: index,
        imageUrl: images.thumbnail || '',
        rowTitle: context,
      };
      navigateToPlay(
        guid,
        {
          viewContext: context,
          listItemContext,
          pageId: this.pageId,
        },
        undefined,
        false,
      );
    } else {
      this.$fatalError();
    }
  }

  private _handleAdPodStart() {
    AppData?.device.onAdPodStart();
    const isAdPreroll = this._Player.isAdPreroll();
    const adDuration = this._Player.adDuration() || 0;

    this._VideoOverlay.adStarted(adDuration);
    this._AdCountdown.patch({
      visible: true,
      isPreroll: isAdPreroll,
      countdown: adDuration,
    });

    this._announcerRefresh();
  }

  private _handleAdPodEnd() {
    AppData?.device.onAdPodEnd();
    this._AdCountdown.visible = false;
    const videoDuration = Number(this._video?.durationSecs) || 0;

    this._VideoOverlay.adBreakEnded(videoDuration);
    this._Player.handleAdPodEnded();
  }

  private _isSeeking() {
    const isSeeking = this._VideoOverlay.isVideoSeeking();
    return isSeeking;
  }

  private startPauseAd() {
    const mediaContent = this.pageData?.mediaContent;
    const state = this._getState();

    if (
      !mediaContent ||
      !isVideo(mediaContent) ||
      state === 'EndCard' ||
      this._VideoOverlay.isInMenu() ||
      this._Player.isPlaying() ||
      this._isAdPlaying ||
      this._isSeeking() ||
      this._buffering ||
      AppData?.modalService.isInHistory('AyswModal')
    ) {
      return;
    }

    const video = mediaContent as Video;
    const { guid } = video;
    const csid = AppData?.adParams?.csid.vod ?? '';

    this._PauseAd.start({
      guid,
      playerWidth: this._Player.w,
      playerHeight: this._Player.h,
      csid: csid,
    });
  }

  private toggleOverlayInfo(enable: boolean) {
    if (enable) {
      this._VideoOverlay.enableOverlay();
      this._AdCountdown.patch({ alpha: 1 });
    } else {
      this._VideoOverlay.disableOverlay();
      this._AdCountdown.patch({ alpha: 0 });
    }
  }

  static override _states() {
    return [
      ...super._states(),
      class EndCard extends this {
        override $enter() {
          this._PauseAd.stop();
          this._VideoOverlay.hide();
          this._EndCard.setSmooth('alpha', 1);
          this.updateBufferingState();
        }

        override $exit() {
          this._EndCard.setSmooth('alpha', 0);
        }

        override _getFocused() {
          return this._EndCard;
        }

        override _captureBack() {
          this.exitPlayer();
        }
      },
      class PauseAd extends this {
        override $enter() {
          this._VideoOverlay.hide();
          this.updateBufferingState();
        }

        override _handleMediaPlay() {
          this.stopPauseAd();
          return super._handleMediaPlay();
        }

        override _handleMediaPause() {
          this.stopPauseAd();
          return super._handleMediaPause();
        }

        override _handleEnter() {
          this.stopPauseAd();
          super._play();
        }

        override _captureBack() {
          this.stopPauseAd();
        }

        private stopPauseAd() {
          this._setState('Interactive');
          this._PauseAd.stop();
        }
      },
    ];
  }
}
